import { WellGeothermalFieldsEdit } from '../../../../types/form-types'
import { useWellState } from '../../../context/well-context'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { CheckableField, CheckableFields, FormField, Heading, InputWidth, TextInput } from '@msaf/core-react'

interface WellGeothermalDetailsInterface {
  fields: Partial<WellGeothermalFieldsEdit>
  isLoading: boolean
  setValue: SetValue<WellGeothermalFieldsEdit>
  validationErrors: ValidationErrors
}

export default function WellGeothermalDetailsEdit({
  fields,
  isLoading,
  validationErrors,
  setValue,
}: WellGeothermalDetailsInterface) {
  const {
    wellState: { isGeothermalWell, isLegacyWell },
  } = useWellState()
  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Details
      </Heading>
      <FormField
        isSkeleton={isLoading}
        label='Geothermal ID'
        htmlFor='geothermalId'
        validationMessages={validationErrors?.geothermalId}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='geothermalId'
          value={`${fields.geothermalId ?? ''}`}
          setValue={(value) => setValue('geothermalId', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Geothermal field'
        htmlFor='geothermalField'
        validationMessages={validationErrors?.geothermalField}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='geothermalField'
          value={`${fields.geothermalField ?? ''}`}
          setValue={(value) => setValue('geothermalField', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='True vertical depth (m)'
        htmlFor='trueVerticalDepth'
        validationMessages={validationErrors?.trueVerticalDepth}
        isRequired={isGeothermalWell && !isLegacyWell}
      >
        <TextInput
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          type='number'
          onWheel={preventChangeOnScroll}
          id='trueVerticalDepth'
          value={`${fields.trueVerticalDepth ?? ''}`}
          setValue={(value) => setValue('trueVerticalDepth', value)}
          isInvalid={!!validationErrors?.trueVerticalDepth}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Wellhead pressure (kPa)'
        htmlFor='wellheadPressure'
        validationMessages={validationErrors?.wellheadPressure}
      >
        <TextInput
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          type='number'
          onWheel={preventChangeOnScroll}
          id='wellheadPressure'
          value={`${fields.wellheadPressure ?? ''}`}
          setValue={(value) => setValue('wellheadPressure', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Well completion signoff standard'
        labelId='wellCompletionSignoffStandard'
        validationMessages={validationErrors?.wellCompletionSignoffStandard}
      >
        <LookupField
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          labelledBy='wellCompletionSignoffStandard'
          fieldId='wellCompletionSignoffStandard'
          value={`${fields.wellCompletionSignoffStandard ?? ''}`}
          setValue={({ value }) => setValue('wellCompletionSignoffStandard', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Well completion signoff by'
        htmlFor='wellCompletionSignoffBy'
        validationMessages={validationErrors?.wellCompletionSignoffBy}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='wellCompletionSignoffBy'
          value={`${fields.wellCompletionSignoffBy ?? ''}`}
          setValue={(value) => setValue('wellCompletionSignoffBy', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Cross sectional construction plan'
        htmlFor='crossSectionalConstructionPlan'
        validationMessages={validationErrors?.crossSectionalConstructionPlan}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='crossSectionalConstructionPlan'
          value={`${fields.crossSectionalConstructionPlan ?? ''}`}
          setValue={(value) => setValue('crossSectionalConstructionPlan', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Site plan'
        htmlFor='sitePlan'
        validationMessages={validationErrors?.sitePlan}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='sitePlan'
          value={`${fields.sitePlan ?? ''}`}
          setValue={(value) => setValue('sitePlan', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Master value'
        htmlFor='masterValve'
        validationMessages={validationErrors?.masterValve}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='masterValve'
          value={`${fields.masterValve ?? ''}`}
          setValue={(value) => setValue('masterValve', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Side value'
        htmlFor='sideValve'
        validationMessages={validationErrors?.sideValve}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='sideValve'
          value={`${fields.sideValve ?? ''}`}
          setValue={(value) => setValue('sideValve', value)}
        />
      </FormField>
      <FormField isSkeleton={isLoading} label='Cellar' htmlFor='cellar' validationMessages={validationErrors?.cellar}>
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='cellar'
          value={`${fields.cellar ?? ''}`}
          setValue={(value) => setValue('cellar', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Downhole formation conditions'
        htmlFor='downholeFormationConditions'
        validationMessages={validationErrors?.downholeFormationConditions}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='downholeFormationConditions'
          value={`${fields.downholeFormationConditions ?? ''}`}
          setValue={(value) => setValue('downholeFormationConditions', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Cement type'
        labelId='cementType'
        validationMessages={validationErrors?.cementType}
      >
        <TextInput
          isSkeleton={isLoading}
          id='cementType'
          type='text'
          value={`${fields.cementType ?? ''}`}
          setValue={(value) => setValue('cementType', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Hole direction (degrees)'
        htmlFor='holeDirectionAzimuth'
        validationMessages={validationErrors?.holeDirectionAzimuth}
      >
        <TextInput
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          type='number'
          onWheel={preventChangeOnScroll}
          id='holeDirectionAzimuth'
          value={`${fields.holeDirectionAzimuth ?? ''}`}
          setValue={(value) => setValue('holeDirectionAzimuth', value)}
          isInvalid={!!validationErrors?.holeDirectionAzimuth}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasUnrecoveredFish}
        isSkeleton={isLoading}
        label='Unrecovered fish'
        labelId='hasUnrecoveredFishLabel'
      >
        <CheckableFields ariaLabelledBy='hasUnrecoveredFishLabel' isHorizontal>
          <CheckableField
            id='hasUnrecoveredFishYes'
            name='hasUnrecoveredFish'
            describedBy='hasUnrecoveredFish'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasUnrecoveredFish ?? false}
            onChange={() => setValue('hasUnrecoveredFish', true)}
          />
          <CheckableField
            id='hasUnrecoveredFishNo'
            name='hasUnrecoveredFish'
            describedBy='hasUnrecoveredFish'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasUnrecoveredFish === false}
            onChange={() => setValue('hasUnrecoveredFish', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Unrecovered fish details'
        htmlFor='unrecoveredFishDetails'
        validationMessages={validationErrors?.unrecoveredFishDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='unrecoveredFishDetails'
          value={`${fields.unrecoveredFishDetails ?? ''}`}
          setValue={(value) => setValue('unrecoveredFishDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasAnnularFlowOutsideCasings}
        isSkeleton={isLoading}
        label='Annular flow outside casings'
        labelId='hasAnnularFlowOutsideCasingsLabel'
      >
        <CheckableFields ariaLabelledBy='hasAnnularFlowOutsideCasingsLabel' isHorizontal>
          <CheckableField
            id='hasAnnularFlowOutsideCasingsYes'
            name='hasAnnularFlowOutsideCasings'
            describedBy='hasAnnularFlowOutsideCasings'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasAnnularFlowOutsideCasings ?? false}
            onChange={() => setValue('hasAnnularFlowOutsideCasings', true)}
          />
          <CheckableField
            id='hasAnnularFlowOutsideCasingsNo'
            name='hasAnnularFlowOutsideCasings'
            describedBy='hasAnnularFlowOutsideCasings'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasAnnularFlowOutsideCasings === false}
            onChange={() => setValue('hasAnnularFlowOutsideCasings', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Annular flow outside casings details'
        htmlFor='annularFlowOutsideCasingsDetails'
        validationMessages={validationErrors?.annularFlowOutsideCasingsDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='annularFlowOutsideCasingsDetails'
          value={`${fields.annularFlowOutsideCasingsDetails ?? ''}`}
          setValue={(value) => setValue('annularFlowOutsideCasingsDetails', value)}
        />
      </FormField>
      <FormField
        validationMessages={validationErrors?.hasChemicalDepositionScale}
        isSkeleton={isLoading}
        label='Chemical deposition scale'
        labelId='hasChemicalDepositionScaleLabel'
      >
        <CheckableFields ariaLabelledBy='hasChemicalDepositionScaleLabel' isHorizontal>
          <CheckableField
            id='hasChemicalDepositionScaleYes'
            name='hasChemicalDepositionScale'
            describedBy='hasChemicalDepositionScale'
            label='Yes'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasChemicalDepositionScale ?? false}
            onChange={() => setValue('hasChemicalDepositionScale', true)}
          />
          <CheckableField
            id='hasChemicalDepositionScaleNo'
            name='hasChemicalDepositionScale'
            describedBy='hasChemicalDepositionScale'
            label='No'
            isSkeleton={isLoading}
            type='radio'
            checked={fields.hasChemicalDepositionScale === false}
            onChange={() => setValue('hasChemicalDepositionScale', false)}
          />
        </CheckableFields>
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Chemical deposition scale details'
        htmlFor='chemicalDepositionScaleDetails'
        validationMessages={validationErrors?.chemicalDepositionScaleDetails}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='chemicalDepositionScaleDetails'
          value={`${fields.chemicalDepositionScaleDetails ?? ''}`}
          setValue={(value) => setValue('chemicalDepositionScaleDetails', value)}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Enthalpy (kj/kg)'
        htmlFor='enthalpy'
        isRequired={isGeothermalWell && !isLegacyWell}
        validationMessages={validationErrors?.enthalpy}
      >
        <TextInput
          isSkeleton={isLoading}
          fieldSize={InputWidth.large}
          type='number'
          onWheel={preventChangeOnScroll}
          id='enthalpy'
          value={`${fields.enthalpy ?? ''}`}
          setValue={(value) => setValue('enthalpy', value)}
          isInvalid={!!validationErrors?.enthalpy}
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Water quality'
        htmlFor='waterQuality'
        validationMessages={validationErrors?.waterQuality}
      >
        <TextInput
          isSkeleton={isLoading}
          type='text'
          id='waterQuality'
          value={`${fields.waterQuality ?? ''}`}
          setValue={(value) => setValue('waterQuality', value)}
        />
      </FormField>
    </>
  )
}
